import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Popover } from '~/components/ui/popover';
import { useApprovals } from '~/hooks/queries/use-approvals';
import { useProBuilderEnabled } from '~/hooks/queries/use-feature-flags';
import { useCodingEnabled } from '~/hooks/queries/use-tenant';
import { useProBuilderOpenStore } from '~/lib/stores/pro-builder-open-store';
import { BuildButton } from '../build-button';
import { firstAsking, resolveAskChip } from '../state-mapping';
import { useChatActivity } from '../use-chat-activity';
import { useOpenBuild } from '../use-open-build';
import { ActivityBoardLegacy } from './activity-board-legacy';
import { AskChipLegacy } from './ask-chip-legacy';
import { NewChatButtonLegacy } from './new-chat-button-legacy';
import { StatusClusterLegacy } from './status-cluster-legacy';
import '../top-bar.css';
import './top-bar-legacy.css';

interface ChatTopBarLegacyProps {
  /** Starts a brand-new chat — the header owns this mechanism. */
  onNewChat: () => void;
  /** Opens an existing chat by session id, from any page. */
  onOpenChat: (sessionId: string, title: string | null) => void;
}

/**
 * FRONT-3223: the top bar as on `origin/main`, rendered while
 * `eddy.ui-refactoring` is OFF — the 2026-08-14 new-chatbox-topbar design: New
 * chat button, Build button, the status cluster with its activity board
 * (count-pill header, "New chat / Open all chats →" footer), and ONE ask
 * chip for the first open question. The reworked bar (`../chat-top-bar.tsx`,
 * FRONT-3144) replaces all of that with one chip per question, the status
 * cluster on the far right and the window tabs.
 *
 * Two deliberate differences from the `origin/main` file:
 * - It takes the same props as the reworked bar (`onNewChat`, `onOpenChat`)
 *   so `app-header.tsx` mounts either without a branch; the board footer's
 *   "Open all chats" navigates to `/chats` from here.
 * - It does NOT publish the browser tab's "(N)" count. The attention store
 *   has ONE writer since FRONT-3143, the sidebar's `useSidebarAsking`; a
 *   second one here could zero a count the sidebar still shows.
 *
 * Never imports the window store or the windows feature: with the flags off
 * there are no windows, and the legacy bar must not depend on them.
 */
export function ChatTopBarLegacy({ onNewChat, onOpenChat }: ChatTopBarLegacyProps) {
  const [boardOpen, setBoardOpen] = useState(false);
  const openBuild = useOpenBuild();
  const navigate = useNavigate();

  // FRONT-1681: Building Sessions fold into the same board, gated on
  // `useCodingEnabled()` so a deployment with coding off doesn't poll
  // `/api/coding/sessions` every 15s from this always-mounted header.
  const codingEnabled = useCodingEnabled();
  // FRONT-3040: the Pro Builder row shows for exactly the people the sidebar
  // shows its Pro Builder entry to.
  const proBuilderEnabled = useProBuilderEnabled();
  const { barChats, groups, counts } = useChatActivity({
    includeBuilds: codingEnabled,
    includeProBuilder: proBuilderEnabled,
  });
  const { data: approvals = [] } = useApprovals();

  const askingChat = useMemo(() => firstAsking(barChats), [barChats]);
  const askChip = useMemo(() => resolveAskChip(askingChat, approvals), [askingChat, approvals]);

  // Global ⌥N / Alt+N — matched on e.code because on macOS Option+N types a
  // dead key and e.key is not "n". Cmd/Ctrl+N is browser-reserved. Keep in
  // sync with NEW_CHAT_SHORTCUT_LABEL in `../new-chat-button.tsx`.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (!e.altKey || e.metaKey || e.ctrlKey || e.code !== 'KeyN') return;
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      e.preventDefault();
      onNewChat();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onNewChat]);

  // ⌥B / Alt+B — the Build button's twin, registered only while building is
  // on for the tenant. Stands down while a full-screen overlay
  // (`role="dialog" aria-modal`) is open, so it never paints a second window
  // on top of a first; Radix popovers carry no `aria-modal`, so the activity
  // board's own popover is let through.
  useEffect(() => {
    if (!codingEnabled) return;
    function onKeyDown(e: KeyboardEvent) {
      if (!e.altKey || e.metaKey || e.ctrlKey || e.code !== 'KeyB') return;
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      if (document.querySelector('[role="dialog"][aria-modal]')) return;
      e.preventDefault();
      openBuild();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [codingEnabled, openBuild]);

  function openFromBoard(sessionId: string, title: string | null) {
    setBoardOpen(false);
    onOpenChat(sessionId, title);
  }

  function openBuildFromBoard(sessionId: string, _title: string | null) {
    setBoardOpen(false);
    openBuild(sessionId);
  }

  // FRONT-3040: the window is mounted once in the app shell, so this works
  // from any page.
  function openProBuilderFromBoard() {
    setBoardOpen(false);
    useProBuilderOpenStore.getState().openProBuilder();
  }

  function newFromBoard() {
    setBoardOpen(false);
    onNewChat();
  }

  function allChatsFromBoard() {
    setBoardOpen(false);
    navigate('/chats');
  }

  return (
    <div className="eddy-topbar flex min-w-0 flex-1 items-center gap-[10px]">
      <NewChatButtonLegacy onClick={onNewChat} />
      {/* Gated on the same `useCodingEnabled()` the board's build rows are. */}
      {codingEnabled && <BuildButton onClick={() => openBuild()} />}
      <Popover open={boardOpen} onOpenChange={setBoardOpen}>
        <StatusClusterLegacy counts={counts} />
        <ActivityBoardLegacy
          groups={groups}
          onOpen={openFromBoard}
          onOpenBuild={openBuildFromBoard}
          onOpenProBuilder={openProBuilderFromBoard}
          onNew={newFromBoard}
          onAllChats={allChatsFromBoard}
        />
      </Popover>
      {askChip && (
        <AskChipLegacy
          label={askChip.label}
          action={askChip.action}
          // FRONT-1681: a Building Session goes to openBuild, never
          // onOpenChat; FRONT-3040: the Pro Builder row opens its window.
          onClick={() => {
            if (askChip.kind === 'pro-builder') openProBuilderFromBoard();
            else if (askChip.kind === 'build') openBuild(askChip.sessionId);
            else onOpenChat(askChip.sessionId, askChip.title);
          }}
        />
      )}
    </div>
  );
}
